import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Person,
  Room,
  Assignment,
  LockOpen,
  VpnKey,
  ViewList,
  ViewModule,
  Refresh
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ORDER_TYPES } from '../data/orderTypes';
import { dataverseService } from '../services/dataverseService';
import { handleDataverseLoadError, getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { loadDataverseWithDemoFallback, DEMO_DATA_BANNER_TEXT } from '../lib/loadDataverseData';
import { isOrdersEnabled } from '../config/features';
import KeyRequestDialog from '../components/KeyRequestDialog';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { hasAzureMapsKey } from '../services/geocodeService';
import { KEY_REQUEST_SCOPE } from '../config/keyRequest';

const ResidentCard = ({ resident, onEdit, onDelete, onCreateOrder, onManageKeys }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  return (
    <Card className="card-hover fade-in">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" component="div" fontWeight="600">
                {resident.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {resident.residentId}
                {resident.nin && ` • NIN: ${resident.nin}`}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClick}>
            <MoreVert />
          </IconButton>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Room sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2">
                {resident.room}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Chip
              label={`EPJ ID: ${resident.epjId || 'Ikke oppgitt'}`}
              color={resident.epjId ? 'primary' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Assignment sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2">
            {resident.assignedLocks.length} lås(er) tilordnet
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary">
          Registrert: {new Date(resident.admissionDate).toLocaleDateString('no-NO')}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Button
            size="small"
            startIcon={<VpnKey />}
            variant="outlined"
            onClick={() => onManageKeys(resident)}
          >
            Nøkler til lås
          </Button>
          {isOrdersEnabled() && (
            <Button
              size="small"
              startIcon={<LockOpen />}
              variant="outlined"
              onClick={() => onCreateOrder(resident)}
            >
              Bestill ny lås
            </Button>
          )}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={() => { handleClose(); onEdit(resident); }}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rediger</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); onDelete(resident); }}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Slett</ListItemText>
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

const ResidentRow = ({ resident, onEdit, onDelete, onCreateOrder, onManageKeys }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200, flex: '1 1 200px' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
          <Person fontSize="small" />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {resident.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            ID: {resident.residentId}
            {resident.nin && ` • NIN: ${resident.nin}`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '1 1 120px', minWidth: 100 }}>
        <Room sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" noWrap>
          {resident.room}
        </Typography>
      </Box>

      <Chip
        label={`${resident.assignedLocks?.length || 0} lås(er)`}
        size="small"
        variant="outlined"
        icon={<Assignment />}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, ml: 'auto', alignItems: 'center' }}>
        <Button size="small" startIcon={<VpnKey />} onClick={() => onManageKeys(resident)}>
          Nøkler
        </Button>
        {isOrdersEnabled() && (
          <Button size="small" startIcon={<LockOpen />} onClick={() => onCreateOrder(resident)}>
            Bestill lås
          </Button>
        )}
        <IconButton
          size="small"
          aria-label={`Handlinger for ${resident.name}`}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>

      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onEdit(resident);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rediger</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onDelete(resident);
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Slett</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

const ResidentsPage = () => {
  const navigate = useNavigate();
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingResident, setEditingResident] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    residentId: '',
    room: '',
    epjId: '',
    nin: '',
    admissionDate: new Date().toISOString().split('T')[0]
  });
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  
  // New state for confirmation dialogs and loading
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [residentToDelete, setResidentToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('dataverse');
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [keyDialogResident, setKeyDialogResident] = useState(null);

  const loadResidents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, source } = await loadDataverseWithDemoFallback(
        () => dataverseService.getResidentsWithLocks(),
        async () => {
          const { mockApi } = await import('../data/demoData');
          return mockApi.getResidents();
        }
      );
      setResidents(data);
      setDataSource(source);
    } catch (loadError) {
      console.error('Error loading residents:', loadError);
      if (handleDataverseLoadError(loadError, navigate)) {
        return;
      }
      setResidents([]);
      setDataSource('dataverse');
      setError(getDataverseErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadResidents();
  }, [loadResidents]);

  const handleAddResident = () => {
    setEditingResident(null);
    setFormData({
      name: '',
      residentId: '', // Will be auto-generated
      room: '',
      epjId: '',
      nin: '',
      admissionDate: new Date().toISOString().split('T')[0]
    });
    setAddressConfirmed(false);
    setOpenDialog(true);
  };

  const handleEditResident = async (resident) => {
    try {
      setLoadingEditData(true);
      // Fetch fresh data for this specific resident from Dataverse
      const freshData = await dataverseService.getResidentsWithLocks();
      const freshResident = freshData.find(r => r.residentId === resident.residentId);
      
      if (freshResident) {
        setEditingResident(freshResident);
        setFormData({
          name: freshResident.name,
          residentId: freshResident.residentId,
          room: freshResident.room || '',
          epjId: freshResident.epjId || '',
          nin: freshResident.nin || '',
          admissionDate: freshResident.admissionDate
        });
        setAddressConfirmed(Boolean(freshResident.room));
      } else {
        // Fallback to cached data if fresh data not found
        setEditingResident(resident);
        setFormData({
          name: resident.name,
          residentId: resident.residentId,
          room: resident.room || '',
          epjId: resident.epjId || '',
          nin: resident.nin || '',
          admissionDate: resident.admissionDate
        });
        setAddressConfirmed(Boolean(resident.room));
      }
    } catch (error) {
      console.error('Error fetching fresh resident data:', error);
      // Fallback to cached data if API fails
      setEditingResident(resident);
      setFormData({
        name: resident.name,
        residentId: resident.residentId,
        room: resident.room || '',
        epjId: resident.epjId || '',
        nin: resident.nin || '',
        admissionDate: resident.admissionDate
      });
      setAddressConfirmed(Boolean(resident.room));
    } finally {
      setLoadingEditData(false);
    }
    setOpenDialog(true);
  };

  const handleSaveResident = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.name?.trim()) {
        setError('Navn er påkrevd.');
        setSaving(false);
        return;
      }
      if (!formData.room?.trim() || !addressConfirmed) {
        setError(
          hasAzureMapsKey()
            ? 'Velg en adresse med husnummer fra forslagslisten. Leilighet/boenhet kan legges til etter valget.'
            : 'Adresseoppslag er ikke konfigurert (REACT_APP_AZURE_MAPS_KEY mangler).'
        );
        setSaving(false);
        return;
      }
      
      if (editingResident) {
        if (dataSource === 'demo') {
          const { mockApi } = await import('../data/demoData');
          await mockApi.updateResident(editingResident.residentId, formData);
        } else {
          await dataverseService.residentService.update(editingResident.residentId, formData);
        }
        await loadResidents();
      } else {
        const { residentId, ...residentData } = formData;
        if (dataSource === 'demo') {
          const { mockApi } = await import('../data/demoData');
          await mockApi.createResident(residentData);
        } else {
          await dataverseService.residentService.create(residentData);
        }
        await loadResidents();
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving resident:', error);
      setError('Feil ved lagring av innbygger. Prøv igjen.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (resident) => {
    setResidentToDelete(resident);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!residentToDelete) return;
    
    try {
      setDeleting(true);
      setError(null);
      if (dataSource === 'demo') {
        const { mockApi } = await import('../data/demoData');
        await mockApi.deleteResident(residentToDelete.residentId);
        await loadResidents();
      } else {
        await dataverseService.residentService.delete(residentToDelete.residentId);
        await loadResidents();
      }
      setDeleteConfirmOpen(false);
      setResidentToDelete(null);
    } catch (error) {
      console.error('Error deleting resident:', error);
      setError('Feil ved sletting av innbygger. Prøv igjen.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setResidentToDelete(null);
  };

  const filteredResidents = residents.filter((resident) => {
    const q = searchTerm.toLowerCase();
    return (
      resident.name.toLowerCase().includes(q) ||
      resident.residentId.toLowerCase().includes(q) ||
      (resident.room || '').toLowerCase().includes(q) ||
      (resident.nin && resident.nin.toLowerCase().includes(q))
    );
  });

  const handleCreateLockSetupOrder = (resident) => {
    navigate('/orders', {
      state: {
        orderPreset: {
          type: ORDER_TYPES.SETUP,
          residentId: resident.residentId,
          location: resident.room,
          description: `Oppsett av ny lås for ${resident.name}`
        }
      }
    });
  };

  const handleManageKeys = (resident) => {
    setKeyDialogResident(resident);
    setKeyDialogOpen(true);
  };

  const handleCloseKeyDialog = () => {
    setKeyDialogOpen(false);
    setKeyDialogResident(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1" fontWeight="600">
          Innbyggere
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={loadResidents}
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Oppdaterer…' : 'Oppdater'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddResident}
            sx={{ borderRadius: 2 }}
          >
            Legg til Innbygger
          </Button>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={viewMode}
            onChange={(_, next) => {
              if (next) setViewMode(next);
            }}
          >
            <ToggleButton value="list">
              <ViewList sx={{ mr: 0.75 }} />
              Liste
            </ToggleButton>
            <ToggleButton value="tiles">
              <ViewModule sx={{ mr: 0.75 }} />
              Kort
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {dataSource === 'demo' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {DEMO_DATA_BANNER_TEXT}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                placeholder="Søk etter navn, ID eller adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`${filteredResidents.length} av ${residents.length}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {viewMode === 'tiles' ? (
        <Grid container spacing={3}>
          {filteredResidents.map((resident) => (
            <Grid item xs={12} sm={6} md={4} key={resident.id}>
              <ResidentCard
                resident={resident}
                onEdit={handleEditResident}
                onDelete={handleDeleteClick}
                onCreateOrder={handleCreateLockSetupOrder}
                onManageKeys={handleManageKeys}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Stack spacing={1}>
          {filteredResidents.map((resident) => (
            <ResidentRow
              key={resident.id}
              resident={resident}
              onEdit={handleEditResident}
              onDelete={handleDeleteClick}
              onCreateOrder={handleCreateLockSetupOrder}
              onManageKeys={handleManageKeys}
            />
          ))}
        </Stack>
      )}

      {filteredResidents.length === 0 && !loading && (
        <Card sx={{ mt: viewMode === 'list' ? 2 : 0 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Ingen innbyggere funnet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Prøv å justere søkekriteriene eller legg til en ny innbygger
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingResident ? (
            loadingEditData ? 'Henter data...' : 'Rediger Innbygger'
          ) : 'Legg til Ny Innbygger'}
        </DialogTitle>
        <DialogContent>
          {loadingEditData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Henter nyeste data fra Dataverse...
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Navn"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            {editingResident && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Innbygger ID"
                  value={formData.residentId}
                  disabled
                  helperText="ID genereres automatisk for nye innbyggere"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <AddressAutocomplete
                label="Adresse"
                value={formData.room}
                required
                onChange={({ address, confirmed }) => {
                  setFormData((prev) => ({ ...prev, room: address }));
                  setAddressConfirmed(confirmed);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="NIN (valgfritt)"
                value={formData.nin}
                onChange={(e) => setFormData({ ...formData, nin: e.target.value })}
                placeholder="11 siffer"
                helperText={editingResident ? `Eksisterende: ${editingResident.nin || 'Ikke oppgitt'}` : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="EPJ ID"
                value={formData.epjId}
                onChange={(e) => setFormData({ ...formData, epjId: e.target.value })}
                placeholder="Oppgi EPJ ID"
                helperText="Elektronisk pasientjournal ID"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Innskrivningsdato"
                value={formData.admissionDate}
                onChange={(e) => setFormData({ ...formData, admissionDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={saving || loadingEditData}>
            Avbryt
          </Button>
          <Button 
            onClick={handleSaveResident} 
            variant="contained"
            disabled={saving || loadingEditData}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Lagrer...' : (editingResident ? 'Oppdater' : 'Lagre')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Bekreft sletting
        </DialogTitle>
        <DialogContent>
          <Typography>
            Er du sikker på at du vil slette innbygger <strong>{residentToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Denne handlingen kan ikke angres.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Avbryt
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Sletter...' : 'Slett'}
          </Button>
        </DialogActions>
      </Dialog>

      <KeyRequestDialog
        open={keyDialogOpen}
        onClose={handleCloseKeyDialog}
        scope={KEY_REQUEST_SCOPE.ALL_LOCKS_FOR_RESIDENT}
        resident={keyDialogResident}
        onSuccess={handleCloseKeyDialog}
      />
    </Box>
  );
};

export default ResidentsPage;
